import React from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  List,
  ListItem,
  Radio,
  FormControl
} from '@mui/material';
import { radioClasses } from '@mui/joy/Radio';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { InterestLevel } from '@gamma/shared/attributes';
import { InterestLevelColors } from '@gamma/shared/icons';
import { InterestLevelLabels } from '@gamma/shared/labels';

/**
 * InterestLevelSelector - Color-based selector for person interest levels
 * Uses colors from aggregator UI
 *
 * @param {string} selectedLevel - Currently selected interest level
 * @param {function} onChange - Callback when selection changes: (level) => void
 * @param {number} iconSize - Size of color circles in pixels (default: 40)
 * @param {boolean} disabled - Whether selector is disabled
 * @param {boolean} showLabel - Whether to show label (default: true)
 * @param {boolean} allowDeselect - Whether clicking selected value deselects it (default: true)
 */
export default function InterestLevelSelector({
  selectedLevel,
  onChange,
  iconSize = 40,
  disabled = false,
  showLabel = true,
  allowDeselect = true
}) {
  const levels = [
    InterestLevel.CRITICAL,
    InterestLevel.HIGH,
    InterestLevel.MEDIUM,
    InterestLevel.LOW,
    InterestLevel.NONE
  ];

  const handleClick = (level) => {
    if (disabled) return;

    if (selectedLevel === level && allowDeselect) {
      onChange(null);
    } else {
      onChange(level);
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            fontSize: '0.8rem',
            color: 'text.secondary'
          }}
        >
          Interest Level
        </Typography>
      )}

      <FormControl disabled={disabled} fullWidth>
        <RadioGroup value={selectedLevel || ''} sx={{ flexDirection: 'row', gap: 1 }}>
          <List
            orientation="horizontal"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              p: 0
            }}
          >
            {levels.map((level) => {
              const isSelected = selectedLevel === level;
              const color = InterestLevelColors[level];
              const label = InterestLevelLabels[level];

              return (
                <ListItem
                  key={level}
                  variant="outlined"
                  onClick={() => handleClick(level)}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    boxShadow: 'sm',
                    bgcolor: color,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    border: isSelected ? '3px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: '50%',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    '&:hover': {
                      borderColor: disabled ? 'divider' : 'primary.light',
                      transform: disabled ? 'none' : 'scale(1.05)',
                      transition: 'all 0.2s'
                    }
                  }}
                  title={label}
                >
                  {isSelected && (
                    <CheckCircleRoundedIcon
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        fontSize: 20,
                        color: 'primary.main',
                        bgcolor: 'background.paper',
                        borderRadius: '50%'
                      }}
                    />
                  )}

                  <Radio
                    value={level}
                    checked={isSelected}
                    disabled={disabled}
                    sx={{ display: 'none' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </RadioGroup>
      </FormControl>
    </Box>
  );
}
